/**
 * Floating-preview window geometry — pure math (2026-08-02 Miles directive: the preview
 * must work as a fully functional draggable/resizable window that stays with the user).
 * Pinned by tests/tools/windowGeometry.test.ts.
 *
 * Z-LAYERING: a preview must sit above the expanded chat or the canvas is buried behind
 * it (2026-08-02 production report). Both rungs are defined with the rest of the app's
 * ladder in src/styles/layers.ts and re-exported here — the header used to restate the
 * chat's own z-index, which had been wrong by two orders of magnitude for months and
 * took the pinning test down with it (2026-09-11).
 */

export { PREVIEW_MODAL_Z, FLOATING_PREVIEW_BASE_Z } from '../../styles/layers';

/**
 * Modal previews open NEAR-FULL-SCREEN by default (2026-08-02 Miles directive:
 * "the preview screen should be larger part of the user's screen dynamically") —
 * the panel fills the viewport minus this margin on every side, on every screen
 * size. The margin doubles as the click-out-to-close backdrop strip.
 */
export const PREVIEW_MODAL_VIEWPORT_MARGIN_PX = 16;

export interface WindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const FLOATING_MIN_WIDTH = 360;
export const FLOATING_MIN_HEIGHT = 260;
/** Minimum strip of a window that must remain reachable inside the viewport. */
export const FLOATING_VISIBLE_MARGIN = 48;

/**
 * Default floating rect: a generous right-side panel that leaves the chat usable.
 *
 * `reservedRightPx` (docked-companion round, 2026-08-24) is the strip of the
 * right edge the DOCKED chat rail owns (dockedCompanion.companionReservedRightPx).
 * A floating preview opened while the chat is docked used to land exactly on top
 * of the rail — same right-aligned geometry, same corner. Sizing and placing
 * against the AVAILABLE width instead drops the window into the remaining space
 * beside the chat, right-aligned against the gutter. Default 0 = the pre-existing
 * behavior, byte for byte (pinned by tests/tools/windowGeometry.test.ts).
 */
export function defaultFloatingRect(
  viewportW: number,
  viewportH: number,
  reservedRightPx: number = 0,
): WindowRect {
  const available = Math.max(FLOATING_MIN_WIDTH, viewportW - Math.max(0, reservedRightPx));
  const width = Math.max(FLOATING_MIN_WIDTH, Math.round(Math.min(available * 0.46, 900)));
  const height = Math.max(FLOATING_MIN_HEIGHT, Math.round(Math.min(viewportH * 0.72, 860)));
  return {
    x: Math.max(8, available - width - 24),
    y: Math.max(8, Math.round((viewportH - height) / 3)),
    width,
    height,
  };
}

/** Clamp a rect so the window stays grabbable: size floors, and the header strip can
 *  never be dragged fully off-screen in any direction. */
export function clampWindowRect(rect: WindowRect, viewportW: number, viewportH: number): WindowRect {
  const width = Math.min(Math.max(rect.width, FLOATING_MIN_WIDTH), Math.max(FLOATING_MIN_WIDTH, viewportW));
  const height = Math.min(Math.max(rect.height, FLOATING_MIN_HEIGHT), Math.max(FLOATING_MIN_HEIGHT, viewportH));
  const minX = FLOATING_VISIBLE_MARGIN - width;
  const maxX = viewportW - FLOATING_VISIBLE_MARGIN;
  const minY = 0; // the header must never go ABOVE the viewport (it is the drag handle)
  const maxY = viewportH - FLOATING_VISIBLE_MARGIN;
  return {
    x: Math.min(Math.max(rect.x, minX), maxX),
    y: Math.min(Math.max(rect.y, minY), maxY),
    width,
    height,
  };
}

/** Apply a drag delta to a window origin, clamped. */
export function applyDrag(
  origin: WindowRect,
  dx: number,
  dy: number,
  viewportW: number,
  viewportH: number,
): WindowRect {
  return clampWindowRect({ ...origin, x: origin.x + dx, y: origin.y + dy }, viewportW, viewportH);
}

/**
 * Which window border a resize gesture grabs. Every edge and corner is live
 * (2026-08-02 Miles directive: "when in floating mode the window size should be
 * entirely customizable").
 */
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * Apply a resize delta from any edge/corner. West/north grabs move the origin
 * with the pointer while the opposite edge stays anchored — including when the
 * size floor kicks in (the un-grabbed edge must never drift).
 */
export function applyEdgeResize(
  origin: WindowRect,
  edge: ResizeEdge,
  dx: number,
  dy: number,
  viewportW: number,
  viewportH: number,
): WindowRect {
  let { x, y, width, height } = origin;
  if (edge.includes('e')) width = origin.width + dx;
  if (edge.includes('s')) height = origin.height + dy;
  if (edge.includes('w')) { width = origin.width - dx; x = origin.x + dx; }
  if (edge.includes('n')) { height = origin.height - dy; y = origin.y + dy; }
  if (width < FLOATING_MIN_WIDTH) {
    if (edge.includes('w')) x -= FLOATING_MIN_WIDTH - width;
    width = FLOATING_MIN_WIDTH;
  }
  if (height < FLOATING_MIN_HEIGHT) {
    if (edge.includes('n')) y -= FLOATING_MIN_HEIGHT - height;
    height = FLOATING_MIN_HEIGHT;
  }
  return clampWindowRect({ x, y, width, height }, viewportW, viewportH);
}

/** Apply a bottom-right resize delta, clamped to floors and viewport (SE shorthand). */
export function applyResize(
  origin: WindowRect,
  dx: number,
  dy: number,
  viewportW: number,
  viewportH: number,
): WindowRect {
  return applyEdgeResize(origin, 'se', dx, dy, viewportW, viewportH);
}

/** Margin a maximized floating window keeps so its borders stay grabbable. */
export const FLOATING_MAXIMIZED_MARGIN = 8;

/**
 * The maximize/restore target: effectively the whole viewport — or, while the
 * chat holds the docked right rail (`reservedRightPx` > 0, docked-companion
 * round 2026-08-24), the whole of what is LEFT of it. Maximizing used to bury
 * the pane the user was watching; now it produces exactly the side-by-side
 * view: file filling the left, conversation on the right.
 */
export function maximizedRect(
  viewportW: number,
  viewportH: number,
  reservedRightPx: number = 0,
): WindowRect {
  const reserved = Math.max(0, reservedRightPx);
  return clampWindowRect(
    {
      x: FLOATING_MAXIMIZED_MARGIN,
      y: FLOATING_MAXIMIZED_MARGIN,
      width: viewportW - reserved - FLOATING_MAXIMIZED_MARGIN * 2,
      height: viewportH - FLOATING_MAXIMIZED_MARGIN * 2,
    },
    viewportW,
    viewportH,
  );
}

/**
 * Slide a window out from under the docked rail, keeping its size when it
 * fits and shrinking it only when it cannot (docked-companion round
 * 2026-08-24). Used when a floating preview asks the chat to dock beside it:
 * the window that just invited the pane in must not be the thing it covers.
 * A zero reservation returns the rect untouched.
 */
export function clearReservedRight(
  rect: WindowRect,
  viewportW: number,
  viewportH: number,
  reservedRightPx: number,
): WindowRect {
  const reserved = Math.max(0, reservedRightPx);
  if (reserved <= 0) return rect;
  const available = Math.max(FLOATING_MIN_WIDTH, viewportW - reserved);
  const width = Math.min(rect.width, Math.max(FLOATING_MIN_WIDTH, available - FLOATING_MAXIMIZED_MARGIN * 2));
  const overhang = rect.x + width - available;
  const x = overhang > 0 ? Math.max(FLOATING_MAXIMIZED_MARGIN, rect.x - overhang) : rect.x;
  return clampWindowRect({ ...rect, x, width }, viewportW, viewportH);
}
